import React, { useCallback, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import {
  BodyText,
  Chip,
  SectionHeader,
  TextFieldCurrency,
  theme,
} from '@sweetgreen/garnish';

import { CheckoutLayoutBlock } from '@order/components';
import { useLocalizationContext } from '@order/Localization';

import { calculateTip } from '../../../Checkout.utils';
import { useCheckoutContext } from '../../../CheckoutProvider';

export const ConnectedCheckoutTipBlock = () => {
  const { cart, payload, handleTip } = useCheckoutContext();
  const orderSubTotal = cart?.ledger?.subtotal ?? 0;
  const selectedTipAmount = payload?.deliveryOrderDetail?.tip ?? 0;

  return (
    <CheckoutTipBlock
      orderSubtotal={orderSubTotal}
      selectedTipAmount={selectedTipAmount}
      setTipAmount={handleTip}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────

export const CheckoutTipBlock = (props: CheckoutTipBlockProps) => {
  const { orderSubtotal, selectedTipAmount, setTipAmount } = props;
  const { t, formatPrice } = useLocalizationContext();

  // ─────────────────────────────────────────────────────────────────────

  const tips = useMemo(
    () => ({
      '10%': {
        label: '10%',
        value: '10',
        amount: calculateTip(orderSubtotal, 10),
        helperText: formatPrice(calculateTip(orderSubtotal, 10), 'USD'),
      },
      '15%': {
        label: '15%',
        value: '15',
        amount: calculateTip(orderSubtotal, 15),
        helperText: formatPrice(calculateTip(orderSubtotal, 15), 'USD'),
      },
      '20%': {
        label: '20%',
        value: '20',
        amount: calculateTip(orderSubtotal, 20),
        helperText: formatPrice(calculateTip(orderSubtotal, 20), 'USD'),
      },
      custom: {
        label: t('checkout.tip.custom'),
        value: 'custom',
        amount: 0,
      },
    }),
    [formatPrice, orderSubtotal, t],
  );

  const { '10%': tip10, '15%': tip15, '20%': tip20, custom: tipCustom } = tips;

  const selectedTipValue =
    getTipType(tips, selectedTipAmount)?.value ?? tipCustom.value;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const onTipTypeChange = useCallback(
    (tipValue: string) => {
      setTipAmount(calculateTipAmount(orderSubtotal, tipValue));
    },
    [setTipAmount, orderSubtotal],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <CheckoutLayoutBlock>
      <SectionHeader heading={t('general.delivery-tip')} />

      <View style={styles.deliveryNoticeContainer}>
        <BodyText size={4}>{t('general.delivery-notice')}</BodyText>
      </View>

      {/* ─── Standard tips ─────────────────────────────────────────────────── */}

      <View testID="checkout.tip.options" style={styles.tipsOptionsContainer}>
        <Chip.Menu value={selectedTipValue} onChange={onTipTypeChange}>
          <Chip
            value={tip10.value}
            label={{ text: tip10.helperText }}
            accessibilityLabel={tip10.label}
          >
            {tip10.label}
          </Chip>

          <Chip
            value={tip15.value}
            label={{ text: tip15.helperText }}
            accessibilityLabel={tip15.label}
          >
            {tip15.label}
          </Chip>

          <Chip
            value={tip20.value}
            label={{ text: tip20.helperText }}
            accessibilityLabel={tip20.label}
          >
            {tip20.label}
          </Chip>

          <Chip
            value={tipCustom.value}
            label={{ spacing: true }}
            accessibilityLabel={tipCustom.label}
          >
            {tipCustom.label}
          </Chip>
        </Chip.Menu>
      </View>

      {/* ─── Custom tip ────────────────────────────────────────────────────── */}

      {selectedTipValue === 'custom' ? (
        <TextFieldCurrency
          accessibilityHint={t('checkout.tip.custom-field-a11y')}
          accessibilityLabel={t('checkout.tip.custom-field-a11y')}
          label={t('checkout.tip.custom-field')}
          defaultValue={selectedTipAmount ? `${selectedTipAmount}` : undefined}
          onChangeText={setTipAmount}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
        />
      ) : null}
    </CheckoutLayoutBlock>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function calculateTipAmount(subtotal: number, tipValue: string) {
  if (tipValue === 'custom') return 0;

  const tip = Number.parseInt(tipValue, 10);
  const isValidTip = Number.isInteger(tip);

  return isValidTip ? calculateTip(subtotal, tip) : 0;
}

function getTipType(tips: Record<string, Tip>, selectedTipAmount: number) {
  return Object.values(tips).find((tip) => tip.amount === selectedTipAmount);
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  deliveryNoticeContainer: {
    paddingTop: theme.spacing.large,
  },
  tipsOptionsContainer: {
    paddingVertical: theme.spacing.large,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type CheckoutTipBlockProps = {
  readonly selectedTipAmount: number;
  readonly setTipAmount: (tipAmount: number) => void;
  readonly orderSubtotal: number;
};

type Tip = {
  readonly value: string;
  readonly amount: number;
  readonly label: string;
  readonly helperText?: string;
};
